import React from 'react';
import { camelizeKeys } from 'humps';
import I18n from 'i18n-js';

import RegionSelect from './addresses/region_select';
import { CountrySelector } from './discovery/country_selector';
import { ICountry } from '../schemas/core-schema';
import { IAddressFormData } from './addresses/address_form_data';
import AddressFieldsConfig from './addresses/address_fields_config';

import FormGroup from '@reverbdotcom/commons/dist/components/form_group';
import FormGroupWithInput from '@reverbdotcom/commons/dist/components/form_group_with_input';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/dist/user_hooks';
import { useViewTracking } from '@reverbdotcom/commons/dist/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/dist/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/dist/elog/mparticle';
import experiments from '@reverbdotcom/commons/dist/experiments';

interface IProps {
  address: IAddressFormData;
  countries: ICountry[];
  defaultCountryCode?: string;
  fieldPrefix?: string;
  hideNameField?: boolean;
  hidePhoneField?: boolean;
  phoneFieldRequired?: boolean;
  addressLabelOverride?: string;
  nameLabelOverride?: string;
  phoneLabel?: JSX.Element | React.ReactNode;
  updateField?: Function;
  shouldTrackView?: boolean;
}

function camelizedCountries(countries) {
  return countries.map(c => camelizeKeys(c)) as ICountry[];
}

function handleInputChange(evt, setState, state, parentUpdateField = undefined) {
  if (parentUpdateField) { parentUpdateField(camelizeKeys(evt)); }

  const name = Object.keys(evt)[0];

  setState({ ...state, [name]: evt[name] });
}

function handleCountryChange(evt, setState, state, parentUpdateField) {
  const fieldsFromConfig = AddressFieldsConfig[evt] || AddressFieldsConfig.default;

  // sets all fields not in new form to blank strings
  const newFields = fieldsFromConfig.map((field) => field.fieldName);
  const oldFields = state.fields.map((field) => field.fieldName);
  const fieldsDifference = oldFields.filter((oldField) => !newFields.includes(oldField));
  const blankFields = fieldsDifference.reduce((prev, fieldName) => ({ ...prev, [fieldName]: '' }), {});

  const newState = { ...state, ...blankFields, country_code: evt, fields: fieldsFromConfig };

  setState(newState);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { fields, ...newStateWithoutFields } = newState;

  if (parentUpdateField) { parentUpdateField(camelizeKeys(newStateWithoutFields)); }
}

function handleRegionChange(evt, setState, state, parentUpdateField = undefined) {
  if (parentUpdateField) { parentUpdateField({ region: evt }); }

  setState({ ...state, region: evt });
}

function buildInputName(prefix, name) {
  if (prefix) {
    return `${prefix}[${name}]`;
  }

  return name;
}

function buildLabel(fieldName, fieldLabel, addressLabelOverride, nameLabelOverride) {
  switch (fieldName) {
    case 'street_address':
      return addressLabelOverride || fieldLabel;
    case 'name':
      return nameLabelOverride || fieldLabel;
    default:
      return fieldLabel;
  }
}

function requirePhoneField(fieldName, phoneFieldRequired) {
  return fieldName === 'phone' && phoneFieldRequired;
}

function fieldLabel(field, user, addressLabelOverride, nameLabelOverride, props) {
  if (field.fieldName === 'phone' && !!props.phoneLabel) {
    return props.phoneLabel;
  }

  return buildLabel(field.fieldName, field.display[user.locale], addressLabelOverride, nameLabelOverride);
}

function renderField(field, props, state, setState, user) {
  const { fieldPrefix, countries, hideNameField, hidePhoneField, nameLabelOverride, addressLabelOverride, updateField } = props;

  if (field.fieldName === 'name' && hideNameField) return null;
  if (field.fieldName === 'phone' && hidePhoneField) return null;

  if (field.fieldName === 'region') {
    return (
      <FormGroup
        key={field.fieldName}
        label={field.display[user.locale]}
      >
        <RegionSelect
          name={buildInputName(fieldPrefix, 'region')}
          countries={camelizedCountries(countries)}
          value={state.region}
          countryCode={state.country_code}
          onChange={evt => handleRegionChange(evt.target.value, setState, state, updateField)}
        />
      </FormGroup>
    );
  }

  return (
    <FormGroupWithInput
      key={field.fieldName}
      required={field.required || requirePhoneField(field.fieldName, props.phoneFieldRequired)}
      label={fieldLabel(field, user, addressLabelOverride, nameLabelOverride, props)}
      inputName={buildInputName(fieldPrefix, field.fieldName)}
      fieldName={field.fieldName}
      value={state[field.fieldName]}
      updateField={e => handleInputChange(e, setState, state, updateField)}
      tagOptional={!field.required && !requirePhoneField(field.fieldName, props.phoneFieldRequired)}
    />
  );
}

function AddressFormFields(props: IProps) {
  const { address, updateField, shouldTrackView } = props;
  const camelizedAddress = camelizeKeys(address);
  const [state, setState] = React.useState({
    name: camelizedAddress.name || '',
    street_address: camelizedAddress.streetAddress || '',
    extended_address: camelizedAddress.extendedAddress || '',
    locality: camelizedAddress.locality || '',
    country_code: camelizedAddress.countryCode || props.defaultCountryCode || '',
    region: camelizedAddress.region || '',
    postal_code: camelizedAddress.postalCode || '',
    phone: camelizedAddress.phone || '',
    fields: AddressFieldsConfig[camelizedAddress.countryCode] || AddressFieldsConfig.default,
  });
  const user = useUser();
  const intlAddressFormCheckoutExp = useExpEnabled(experiments.INTERNATIONAL_ADDRESS_FORM_CHECKOUT);

  useViewTracking({
    componentName: 'AddressFormFields',
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(
      user,
      [
        experiments.INTERNATIONAL_ADDRESS_FORM_CHECKOUT,
      ],
    ),
  }, shouldTrackView);

  if (!intlAddressFormCheckoutExp) {
    return null;
  }

  return (
    <div data-address-fields>
      <FormGroup label={I18n.t('discovery.addressFormFields.country')}>
        <CountrySelector
          inputName={buildInputName(props.fieldPrefix, 'country_code')}
          value={state.country_code}
          onChange={e => handleCountryChange(e, setState, state, updateField)}
          countries={props.countries}
        />
      </FormGroup>

      {state.fields.map((field) => {
        return renderField(field, props, state, setState, user);
      })}
    </div>
  );
}

export default AddressFormFields;
